import { BestRadio } from "best-common-react";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
    getValidNeedsReviewTargetStatuses,
    StatusValidation,
    validateNeedsReviewTransition,
} from "../../../api/RosterApi";
import { useAlert } from "../../../contexts/AlertsContext";
import { useGlobalModal } from "../../../contexts/GlobalModalContext";
import { OutlinedButtonStyled } from "../OutlinedButtonStyled";
import { PrimaryButtonStyled } from "../PrimaryButtonStyled";

const ModalBody = styled.div.attrs(() => ({
    className: "m-4",
}))`
    min-height: 200px;
`;

const PersonnelInfoSection = styled.div.attrs(() => ({
    className: "mb-3",
}))``;

const PersonnelNameStyle = styled.div.attrs(() => ({ className: "mb-1" }))`
    font-family: Helvetica;
    font-size: 16px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.5;
    letter-spacing: normal;
    color: var(--slate);
`;

const AffiliateInfoStyle = styled.div.attrs(() => ({}))`
    font-family: Helvetica;
    font-size: 12px;
    font-weight: 300;
    font-stretch: normal;
    font-style: normal;
    line-height: normal;
    letter-spacing: normal;
    color: var(--slate);
`;

const StatusSelectionSection = styled.div.attrs(() => ({
    className: "mb-3",
}))``;

const SectionTitle = styled.div.attrs(() => ({
    className: "mb-2",
}))`
    color: #92a5ba;
    font-size: 12px;
    font-weight: 600;
`;

const StyledRadioButtonText = styled.div.attrs(() => ({
    className: "d-flex align-self-center mt-1 mr-3",
}))`
    font-family: Helvetica;
    font-weight: normal;
    font-size: 14px;
`;

const LoadingText = styled.div`
    font-family: Helvetica;
    font-size: 14px;
    color: var(--slate);
    font-style: italic;
`;

const ErrorText = styled.div`
    font-family: Helvetica;
    font-size: 14px;
    color: var(--red);
`;

const WarningSection = styled.div`
    background-color: #fff3cd;
    border: 1px solid #ffc107;
    border-radius: 4px;
    padding: 12px;
    margin-bottom: 16px;
`;

const WarningTitle = styled.div`
    font-family: Helvetica;
    font-size: 14px;
    font-weight: bold;
    color: #856404;
    margin-bottom: 8px;
`;

const WarningText = styled.div`
    font-family: Helvetica;
    font-size: 13px;
    color: #856404;
    margin-bottom: 4px;
`;

const DisabledRadioOption = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 8px;
    opacity: 0.5;
    cursor: not-allowed;
`;

const RequirementsList = styled.div`
    margin-left: 32px;
    font-size: 12px;
    color: var(--red);
    margin-top: 4px;
`;

type MoveFromNeedsReviewBodyProps = {
    personnelInfo: PersonnelInfoType;
    selectedStatus: string | null;
    setSelectedStatus: Function;
};

type MoveFromNeedsReviewFooterProps = {
    personnelInfo: PersonnelInfoType;
    selectedStatus: string | null;
    moveFromNeedsReviewCallback: (personnelId: number, newStatus: string) => void;
};

export type PersonnelInfoType = {
    id: number;
    lastName: string;
    firstName: string;
    title: string;
    affiliation: {
        orgAbbreviation: string;
        name: string;
    };
};

const MoveFromNeedsReviewBody: React.FC<MoveFromNeedsReviewBodyProps> = ({
    personnelInfo,
    selectedStatus,
    setSelectedStatus,
}) => {
    const showAlert = useAlert();
    const [validStatuses, setValidStatuses] = useState<string[]>([]);
    const [validationResults, setValidationResults] = useState<Record<string, StatusValidation>>(
        {},
    );
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        Promise.all([
            getValidNeedsReviewTargetStatuses(),
            validateNeedsReviewTransition(personnelInfo.id),
        ])
            .then(([statuses, validations]) => {
                setValidStatuses(statuses);
                setValidationResults(validations);
                setLoading(false);
            })
            .catch(err => {
                console.error("Error fetching statuses and validations:", err);
                setError("Failed to load available statuses. Please try again.");
                setLoading(false);
                showAlert(
                    "Error loading available statuses, Please contact BIGS Systems.",
                    "danger",
                );
            });
    }, [personnelInfo.id]);

    return (
        <ModalBody>
            <PersonnelInfoSection>
                <PersonnelNameStyle>
                    {personnelInfo.lastName}, {personnelInfo.firstName}
                </PersonnelNameStyle>
                <AffiliateInfoStyle>
                    {personnelInfo.title} | {personnelInfo.affiliation.orgAbbreviation} |{" "}
                    {personnelInfo.affiliation.name}
                </AffiliateInfoStyle>
            </PersonnelInfoSection>

            <StatusSelectionSection>
                <SectionTitle>SELECT TARGET STATUS</SectionTitle>
                {loading && <LoadingText>Loading available statuses...</LoadingText>}
                {error && <ErrorText>{error}</ErrorText>}
                {!loading && !error && validStatuses.length > 0 && (
                    <>
                        {/* Show warning if any statuses are invalid */}
                        {Object.values(validationResults).some(v => !v.valid) && (
                            <WarningSection>
                                <WarningTitle>⚠️ Some statuses are not available</WarningTitle>
                                <WarningText>
                                    Certain statuses require completed questionnaires and consent
                                    forms.
                                </WarningText>
                            </WarningSection>
                        )}

                        <div>
                            {validStatuses.map(status => {
                                const validation = validationResults[status];
                                const isValid = validation?.valid !== false;

                                if (!isValid) {
                                    return (
                                        <div key={status}>
                                            <DisabledRadioOption>
                                                <BestRadio
                                                    checked={false}
                                                    disabled={true}
                                                    onChange={() => {}}
                                                />
                                                <StyledRadioButtonText>
                                                    {status} (unavailable)
                                                </StyledRadioButtonText>
                                            </DisabledRadioOption>
                                            {validation?.errorMessage && (
                                                <RequirementsList>
                                                    ✗ {validation.errorMessage}
                                                </RequirementsList>
                                            )}
                                        </div>
                                    );
                                }

                                return (
                                    <div key={status} className="d-flex align-items-center mb-2">
                                        <BestRadio
                                            checked={selectedStatus === status}
                                            onChange={() => setSelectedStatus(status)}
                                        />
                                        <StyledRadioButtonText>{status}</StyledRadioButtonText>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
                {!loading && !error && validStatuses.length === 0 && (
                    <ErrorText>No valid target statuses found.</ErrorText>
                )}
            </StatusSelectionSection>
        </ModalBody>
    );
};

const MoveFromNeedsReviewFooter: React.FC<MoveFromNeedsReviewFooterProps> = ({
    personnelInfo,
    selectedStatus,
    moveFromNeedsReviewCallback,
}) => {
    const { closeModal } = useGlobalModal();
    const showAlert = useAlert();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleMoveStatus = async () => {
        if (!selectedStatus) return;

        setIsSubmitting(true);
        try {
            await moveFromNeedsReviewCallback(personnelInfo.id, selectedStatus);
            closeModal();
        } catch (error) {
            // Handle validation errors from backend
            const errorMessage =
                error?.response?.data?.message ||
                error?.message ||
                "Failed to move status. Please try again.";
            showAlert(errorMessage, "danger");
            setIsSubmitting(false);
        }
    };

    return (
        <div className="d-flex">
            <div className="mr-2">
                <PrimaryButtonStyled
                    width={127}
                    height={26}
                    disabled={!selectedStatus || isSubmitting}
                    onClick={event => {
                        event.stopPropagation();
                        handleMoveStatus();
                    }}
                >
                    {isSubmitting ? "Moving..." : "Move Status"}
                </PrimaryButtonStyled>
            </div>

            <OutlinedButtonStyled
                width={80}
                height={26}
                disabled={isSubmitting}
                onClick={event => {
                    event.stopPropagation();
                    closeModal();
                }}
            >
                Cancel
            </OutlinedButtonStyled>
        </div>
    );
};

MoveFromNeedsReviewBody.displayName = "BodyContent";
MoveFromNeedsReviewFooter.displayName = "FooterContent";

export { MoveFromNeedsReviewBody, MoveFromNeedsReviewFooter };
